import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Alert,
  InputLabel,
  Typography,
  CircularProgress,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store/store';
import {
  changeResetEmail,
  updateResetEmail,
} from '../../features/user/userSlice';
import { resetEmail } from '../../features/user/utils';
import StyledBox from './styled/PasswordReset/StyledBox';
import VerificationCodeForm from './VerificationCodeForm';

const ResetEmail = () => {
  const dispatch: AppDispatch = useDispatch();

  const formData = useSelector((state: RootState) => state.user.resetEmailForm);
  const errors = useSelector(
    (state: RootState) => state.user.resetEmailFormErrors,
  );
  const isSendingVerificationCode = useSelector(
    (state: RootState) => state.user.isSendingVerificationCode,
  );

  const [showVerification, setShowVerification] = useState(false);
  const [displaySuccessMsg, setDisplaySuccessMsg] = useState<boolean>(false);

  
  if (isSendingVerificationCode) {
    return (
      <div
        style={{
          backgroundColor: '#0000005e',
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: '1.25rem',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <CircularProgress sx={{ color: '#FFF' }} size={50} />
        <Typography sx={{ marginTop: '0.9375rem', fontSize: '1.25rem', color: '#FFF' }}>
          Generating code... Please wait!
        </Typography>
      </div>
    );
  }

  const hasErrors = Object.values(errors).some((error) => error !== '');

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    dispatch(resetEmail()).unwrap().then(() => {
      setShowVerification(true);
    }).catch(() => {
      // error is already in state.resetEmailFormErrors.requestError
    });
  };

  const goBack = () => {
    dispatch(changeResetEmail());
  };

  return showVerification && !hasErrors ? (
    <VerificationCodeForm origin='reset-email' />
  ) : (
    <StyledBox>
      <Typography
        sx={{ marginTop: '0.9375rem', marginBottom: '0.375rem' }}
        component='h1'
        variant='h5'
      >
        Add New Email
      </Typography>
      <React.Fragment>
        <Typography textAlign='center'>
          Enter new email and press the button to get the verification code.
        </Typography>
        <Box
          component='form'
          noValidate
          onSubmit={handleSubmit}
          sx={{ mt: 3, width: '100%' }}
        >
          <InputLabel
            htmlFor='email'
            sx={{ color: '#49454f', fontWeight: '500', justifySelf: 'start' }}
          >
            Email Address*
          </InputLabel>
          <TextField
            fullWidth
            id='email'
            required
            placeholder='Add...'
            sx={{ marginTop: 0 }}
            autoFocus
            value={formData.email}
            onChange={(e) =>
              dispatch(
                updateResetEmail({
                  value: e.target.value,
                }),
              )
            }
            error={!!errors.emailError}
            helperText={errors.emailError}
            margin='normal'
          />
          <Button
            type='submit'
            fullWidth
            variant='contained'
            sx={{ mt: 3, mb: 3, textTransform: 'initial' }}
          >
            Get the Code
          </Button>
          <Button
            fullWidth
            variant='outlined'
            sx={{
              textTransform: 'initial',
              color: '#1d1b20',
              borderColor: '#d6d6d6',
              borderRadius: '0rem',
            }}
            onClick={goBack}
          >
            Go Back
          </Button>
        </Box>
      </React.Fragment>
      {errors.requestError && (
        <Alert sx={{ width: '60%' }} severity='error'>
          {errors.requestError}
        </Alert>
      )}
      {displaySuccessMsg && (
        <Alert sx={{ width: '60%' }} severity='success'>
          Password reset succesffully. Redirecting to the Sign In page!
        </Alert>
      )}
    </StyledBox>
  );
};

export default ResetEmail;
